// Command palette (Ctrl/Cmd+K): every mode, channel, action, and export
// reachable by typing.

import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react'
import { useStore } from '../state/store'
import { MODES } from './ModeRail'
import { CHANNELS } from './SidePanel'
import { pickDesignFile, runExport, type ExportFormat, FORMAT_INFO } from './exporters'
import { LOCALES } from '../i18n'
import {
  IconCurve,
  IconDice,
  IconExport,
  IconFit,
  IconGallery,
  IconHistory,
  IconImport,
  IconKeyboard,
  IconLine,
  IconMoon,
  IconPoint,
  IconPolyline,
  IconRedo,
  IconReset,
  IconSun,
  IconUndo,
} from './icons'

interface Command {
  id: string
  title: string
  section: string
  shortcut?: string
  icon?: ComponentType<{ size?: number }>
  run: () => void
}

// Query chars must appear in order; contiguous matches score higher.
function fuzzyScore(query: string, text: string): number | null {
  const q = query.toLowerCase()
  const t = text.toLowerCase()
  if (q.length === 0) return 0
  const sub = t.indexOf(q)
  if (sub >= 0) return 100 - sub
  let ti = 0
  let score = 0
  for (const ch of q) {
    const found = t.indexOf(ch, ti)
    if (found < 0) return null
    score -= found - ti
    ti = found + 1
  }
  return score
}

export function CommandPalette() {
  const store = useStore
  const setOverlay = useStore((s) => s.setOverlay)
  const theme = useStore((s) => s.theme)
  const [query, setQuery] = useState('')
  const [hot, setHot] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  useEffect(() => inputRef.current?.focus(), [])

  const commands = useMemo<Command[]>(() => {
    const s = store.getState()
    const cmds: Command[] = []

    MODES.forEach((m, i) => {
      cmds.push({
        id: `mode-${m.id}`,
        title: `Switch to ${m.label}`,
        section: 'Layout mode',
        shortcut: String(i + 1),
        icon: m.icon,
        run: () =>
          s.commit(`Switch to ${m.label}`, (d) => {
            d.layout.mode = m.id
          }),
      })
    })

    const channelKeys: Record<string, string> = { size: 'S', spacing: 'P', angle: 'A', shape: 'M' }
    CHANNELS.forEach((c) => {
      cmds.push({
        id: `channel-${c.id}`,
        title: `Edit ${c.label} channel`,
        section: 'Channels',
        shortcut: channelKeys[c.id],
        run: () => s.setActiveChannel(c.id),
      })
    })
    const addKinds = [
      { kind: 'point', icon: IconPoint },
      { kind: 'line', icon: IconLine },
      { kind: 'curve', icon: IconCurve },
      { kind: 'polyline', icon: IconPolyline },
    ] as const
    for (const k of addKinds) {
      cmds.push({
        id: `add-${k.kind}`,
        title: `Add ${k.kind} controller`,
        section: 'Channels',
        icon: k.icon,
        run: () =>
          s.setPendingAdd({
            target: 'controller',
            channel: s.activeChannel,
            kind: k.kind,
            firstPoint: null,
            nodes: [],
          }),
      })
    }

    cmds.push(
      {
        id: 'undo',
        title: 'Undo',
        section: 'Document',
        shortcut: 'Ctrl+Z',
        icon: IconUndo,
        run: () => s.undo(),
      },
      {
        id: 'redo',
        title: 'Redo',
        section: 'Document',
        shortcut: 'Ctrl+Shift+Z',
        icon: IconRedo,
        run: () => s.redo(),
      },
      {
        id: 'shuffle-seed',
        title: 'Shuffle seed',
        section: 'Document',
        shortcut: 'R',
        icon: IconDice,
        run: () => {
          s.rememberSeed(s.doc.layout.seed)
          s.commit('Shuffle seed', (d) => {
            d.layout.seed = Math.floor(Math.random() * 1e9) + 1
          })
        },
      },
      {
        id: 'reset',
        title: 'Reset design to defaults',
        section: 'Document',
        icon: IconReset,
        run: () => {
          s.resetDocument()
          s.pushToast('Design reset to defaults. Undo restores it.', 'info')
        },
      },
      {
        id: 'import',
        title: 'Import design file',
        section: 'Document',
        icon: IconImport,
        run: () =>
          pickDesignFile(
            (parsed) => {
              store.getState().loadDocument(parsed, 'Import design')
              store.getState().pushToast('Design imported', 'ok')
            },
            () => store.getState().pushToast('Could not read a Ventholes design from that file', 'error'),
          ),
      },
    )

    cmds.push(
      {
        id: 'export-dialog',
        title: 'Open export dialog',
        section: 'Export',
        shortcut: 'E',
        icon: IconExport,
        run: () => s.setOverlay('export'),
      },
      ...(Object.keys(FORMAT_INFO) as ExportFormat[]).map((f) => ({
        id: `export-${f}`,
        title: `Export ${FORMAT_INFO[f].name} (${FORMAT_INFO[f].ext})`,
        section: 'Export',
        run: () => {
          const state = store.getState()
          const name = runExport(f, state.doc, state.result)
          if (name) state.pushToast(`${name} written`, 'ok')
          else state.pushToast('Nothing generated yet, wait for the preview', 'warn')
        },
      })),
    )

    cmds.push(
      {
        id: 'fit',
        title: 'Fit view',
        section: 'View',
        shortcut: 'F',
        icon: IconFit,
        run: () => s.requestFit(),
      },
      {
        id: 'theme',
        title: theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
        section: 'View',
        icon: theme === 'dark' ? IconSun : IconMoon,
        run: () => s.toggleTheme(),
      },
      {
        id: 'perf',
        title: s.perfMode ? 'Disable performance mode' : 'Enable performance mode',
        section: 'View',
        run: () => s.setPerfMode(!s.perfMode),
      },
      {
        id: 'snap',
        title: s.snap ? 'Disable snapping' : 'Enable snapping',
        section: 'View',
        run: () => s.setSnap(!s.snap),
      },
      {
        id: 'history',
        title: 'Undo history',
        section: 'View',
        shortcut: 'H',
        icon: IconHistory,
        run: () => s.setOverlay('history'),
      },
      {
        id: 'shape-editor',
        title: 'Edit custom shape',
        section: 'View',
        run: () => s.setOverlay('shapeEditor'),
      },
      {
        id: 'gallery',
        title: 'Preset gallery and recipes',
        section: 'View',
        icon: IconGallery,
        run: () => s.setOverlay('gallery'),
      },
      ...LOCALES.map((l) => ({
        id: `lang-${l.id}`,
        title: `Language: ${l.label}`,
        section: 'View',
        run: () => s.setLocale(l.id),
      })),
      {
        id: 'shortcuts',
        title: 'Keyboard shortcuts',
        section: 'View',
        shortcut: '?',
        icon: IconKeyboard,
        run: () => s.setOverlay('shortcuts'),
      },
      {
        id: 'about',
        title: 'About Ventholes',
        section: 'View',
        run: () => s.setOverlay('about'),
      },
    )

    return cmds
  }, [store, theme])

  const filtered = useMemo(() => {
    if (!query.trim()) return commands
    return commands
      .map((c) => ({ c, score: fuzzyScore(query.trim(), c.title) }))
      .filter((x): x is { c: Command; score: number } => x.score !== null)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.c)
  }, [commands, query])

  useEffect(() => setHot(0), [query])

  const run = (cmd: Command) => {
    setOverlay(null)
    cmd.run()
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHot((h) => Math.min(h + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHot((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered[hot]) run(filtered[hot])
    }
  }

  // Keep the hot row in view.
  useEffect(() => {
    listRef.current
      ?.querySelector('[data-hot="true"]')
      ?.scrollIntoView({ block: 'nearest' })
  }, [hot, filtered])

  let lastSection = ''

  return (
    <div className="dialog palette holo-frame" role="dialog" aria-modal="true" aria-label="Command palette">
      <input
        ref={inputRef}
        className="palette-input"
        placeholder="Type a command: mode, channel, export, view..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-expanded="true"
        aria-controls="palette-list"
        aria-activedescendant={filtered[hot] ? `cmd-${filtered[hot].id}` : undefined}
      />
      <ul className="palette-list" id="palette-list" role="listbox" ref={listRef}>
        {filtered.length === 0 && <li className="palette-empty">No matching command</li>}
        {filtered.map((cmd, i) => {
          const showSection = !query.trim() && cmd.section !== lastSection
          lastSection = cmd.section
          const Icon = cmd.icon
          return (
            <li key={cmd.id}>
              {showSection && <div className="palette-section">{cmd.section}</div>}
              <div
                id={`cmd-${cmd.id}`}
                className={i === hot ? 'palette-item hot' : 'palette-item'}
                data-hot={i === hot}
                role="option"
                aria-selected={i === hot}
                onPointerMove={() => setHot(i)}
                onClick={() => run(cmd)}
              >
                {Icon ? <Icon size={15} /> : <span style={{ width: 15 }} />}
                <span className="grow">{cmd.title}</span>
                {cmd.shortcut && <kbd>{cmd.shortcut}</kbd>}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
